import { useEffect, useRef, useState } from 'react';

import { IconCheck, IconChevronDown, IconFilter, IconSearch, IconX } from './icons';
import {
  ALL_CATEGORIES,
  ALL_PRIORITIES,
  ALL_STATUSES,
  CATEGORY_SHORT_LABELS,
  PRIORITY_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
} from './incidentLabels';
import type { IncidentCategory, IncidentPriority, IncidentStatus } from '../../types/extranet-api.types';

interface IncidentsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: IncidentStatus[];
  onStatusChange: (next: IncidentStatus[]) => void;
  category: IncidentCategory[];
  onCategoryChange: (next: IncidentCategory[]) => void;
  priority: IncidentPriority[];
  onPriorityChange: (next: IncidentPriority[]) => void;
  totalCount: number;
}

export const IncidentsFilters = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  category,
  onCategoryChange,
  priority,
  onPriorityChange,
  totalCount,
}: IncidentsFiltersProps) => {
  const [draft, setDraft] = useState(search);

  useEffect(() => {
    setDraft(search);
  }, [search]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (draft !== search) onSearchChange(draft);
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const toggleStatus = (s: IncidentStatus) => {
    onStatusChange(status.includes(s) ? status.filter(x => x !== s) : [...status, s]);
  };

  const advancedCount = category.length + priority.length;
  const hasAnyFilter = search.length > 0 || status.length > 0 || advancedCount > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          value={draft}
          onChange={setDraft}
          onClear={() => {
            setDraft('');
            onSearchChange('');
          }}
        />

        <div className="flex items-center gap-1.5 flex-wrap">
          {ALL_STATUSES.map(s => (
            <StatusChip key={s} status={s} active={status.includes(s)} onClick={() => toggleStatus(s)} />
          ))}
        </div>

        <AdvancedFiltersPopover
          category={category}
          onCategoryChange={onCategoryChange}
          priority={priority}
          onPriorityChange={onPriorityChange}
          activeCount={advancedCount}
        />

        <div className="ml-auto flex items-center gap-3 text-xs text-zinc-500">
          {hasAnyFilter ? (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onSearchChange('');
                onStatusChange([]);
                onCategoryChange([]);
                onPriorityChange([]);
              }}
              className="text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
            >
              Effacer les filtres
            </button>
          ) : null}
          <span className="font-mono tabular-nums">
            {totalCount} <span className="text-zinc-400">incident{totalCount > 1 ? 's' : ''}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const SearchInput = ({
  value,
  onChange,
  onClear,
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
}) => (
  <div className="relative flex-1 max-w-xs">
    <IconSearch
      width={14}
      height={14}
      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
    />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="N° ticket ou course"
      className="w-full h-8 pl-8 pr-8 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 transition-colors"
    />
    {value ? (
      <button
        type="button"
        onClick={onClear}
        aria-label="Effacer la recherche"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
      >
        <IconX width={12} height={12} />
      </button>
    ) : null}
  </div>
);

const StatusChip = ({ status, active, onClick }: { status: IncidentStatus; active: boolean; onClick: () => void }) => {
  const tokens = STATUS_COLORS[status];
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors duration-100 active:scale-[0.98]',
        active
          ? 'bg-zinc-900 text-white border border-zinc-900'
          : 'bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
      ].join(' ')}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white/80' : tokens.dot}`} aria-hidden />
      {STATUS_LABELS[status]}
    </button>
  );
};

const AdvancedFiltersPopover = ({
  category,
  onCategoryChange,
  priority,
  onPriorityChange,
  activeCount,
}: {
  category: IncidentCategory[];
  onCategoryChange: (next: IncidentCategory[]) => void;
  priority: IncidentPriority[];
  onPriorityChange: (next: IncidentPriority[]) => void;
  activeCount: number;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const toggleCategory = (c: IncidentCategory) => {
    onCategoryChange(category.includes(c) ? category.filter(x => x !== c) : [...category, c]);
  };
  const togglePriority = (p: IncidentPriority) => {
    onPriorityChange(priority.includes(p) ? priority.filter(x => x !== p) : [...priority, p]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={[
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium border transition-colors duration-100',
          activeCount > 0
            ? 'bg-zinc-900 text-white border-zinc-900'
            : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
        ].join(' ')}
      >
        <IconFilter width={12} height={12} />
        Filtres
        {activeCount > 0 ? <span className="font-mono tabular-nums">{activeCount}</span> : null}
        <IconChevronDown width={12} height={12} />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-64 bg-white border border-zinc-200 rounded-md shadow-md py-1.5">
          <PopoverGroup label="Catégorie">
            {ALL_CATEGORIES.map(c => (
              <CheckItem
                key={c}
                label={CATEGORY_SHORT_LABELS[c]}
                checked={category.includes(c)}
                onClick={() => toggleCategory(c)}
              />
            ))}
          </PopoverGroup>
          <div className="my-1.5 border-t border-zinc-100" />
          <PopoverGroup label="Priorité">
            {ALL_PRIORITIES.map(p => (
              <CheckItem
                key={p}
                label={PRIORITY_LABELS[p]}
                checked={priority.includes(p)}
                onClick={() => togglePriority(p)}
              />
            ))}
          </PopoverGroup>
        </div>
      ) : null}
    </div>
  );
};

const PopoverGroup = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="px-1.5">
    <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-400 px-2 pt-1 pb-1.5">{label}</p>
    <ul className="flex flex-col gap-0.5">{children}</ul>
  </div>
);

const CheckItem = ({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className="w-full h-8 px-2 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
    >
      <span
        className={[
          'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
          checked ? 'bg-zinc-900 border-zinc-900 text-white' : 'border-zinc-300 bg-white',
        ].join(' ')}
      >
        {checked ? <IconCheck width={10} height={10} strokeWidth={3} /> : null}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  </li>
);
